import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import Countdown from "./Countdown";
import VolunteerCountBadge from "./VolunteerCountBadge";
import { getPhase, nextPitchSession, isPitchLiveNow, votingDeadlineDate, EVENT, buildICS, downloadICS } from "@/lib/phase";

const HERO_BG_FALLBACK = "https://customer-assets.emergentagent.com/job_pitch-and-vote/artifacts/jhyi97br_image.png";

export default function Hero({ settings, externalVotingUrl, heroImage, onWatchLive, onScrollToPitches, onScrollToForms, onScrollToBanners }) {
  const now = new Date();
  const phase = getPhase(settings, now);
  const live = isPitchLiveNow(now);
  const nextSession = nextPitchSession(now);
  const votingDeadline = votingDeadlineDate(settings);

  const headline = useMemo(() => {
    const override = settings?.hero_overrides?.[String(phase)]?.headline;
    if (override) return override;
    if (phase === 1) return "Live Business Showers Pitches Are Coming";
    if (phase === 2) return live ? "Business Showers Pitches Are Live" : "Business Showers Pitches Are Live This Week";
    if (phase === 3) return "The Pitches Are Complete. Now the Community Decides.";
    if (phase === 4) return "Experience Business Showers 2026";
    return "Business Showers 2026 Highlights";
  }, [phase, live, settings]);

  const support = useMemo(() => {
    const override = settings?.hero_overrides?.[String(phase)]?.support;
    if (override) return override;
    if (phase === 1) return "Three nights of live pitches, $10,000 in cash awards, and a community choosing who moves forward. June 16, 17 and 18, 2026 — followed by the VIP Experience on July 17 and the Main Celebration on July 18.";
    if (phase === 2) return "From June 16 through June 18, meet this year's participating entrepreneurs as they present their businesses. Watch live, meet the judges, and cast your $2 vote before voting closes June 22.";
    if (phase === 3) return "Watch the replays, choose the businesses you want to support, and vote by June 22. Every $2 vote moves a business forward.";
    if (phase === 4) return "Friday, July 17 — VIP Panel & Speed-Networking. Saturday, July 18 — Main Business Showers Celebration. Reserve your seat to celebrate this year's finalists.";
    return "Awards delivered. Community fed. Businesses launched. Explore the highlights from this year's Business Showers — and tell us if you'd like to pitch next year.";
  }, [phase, settings]);

  // Primary CTA per phase
  const primaryCtas = useMemo(() => {
    if (phase === 1) return [
      { label: "View Pitch Schedule", action: onScrollToPitches, kind: "amber", testid: "hero-cta-schedule" },
      { label: "Meet the Businesses", action: onScrollToPitches, kind: "outline", testid: "hero-cta-meet" },
      { label: "Vote Now", href: externalVotingUrl, kind: "rose", testid: "hero-cta-vote" },
      { label: "Purchase Event Tickets", action: onScrollToBanners, kind: "outline", testid: "hero-cta-tickets" },
    ];
    if (phase === 2) return [
      ...(live ? [{ label: "▶ Watch Live", action: onWatchLive, kind: "rose", testid: "hero-cta-watch" }] : []),
      { label: "View Today's Schedule", action: onScrollToPitches, kind: "amber", testid: "hero-cta-schedule" },
      { label: "Watch the Replays", href: "/replays", kind: "outline", testid: "hero-cta-replays", internal: true },
      { label: "Vote for a Business", href: externalVotingUrl, kind: "rose", testid: "hero-cta-vote" },
    ];
    if (phase === 3) return [
      { label: "Watch the Replays", href: "/replays", kind: "amber", testid: "hero-cta-replays", internal: true },
      { label: "Cast Your $2 Vote", href: externalVotingUrl, kind: "rose", testid: "hero-cta-vote" },
      { label: "Share the Vote", action: () => navigator.share ? navigator.share({title:"Business Showers",url:externalVotingUrl}).catch(()=>{}) : navigator.clipboard.writeText(externalVotingUrl), kind: "outline", testid: "hero-cta-share" },
    ];
    if (phase === 4) return [
      { label: "Meet the Finalists", action: onScrollToPitches, kind: "amber", testid: "hero-cta-finalists" },
      { label: "Purchase Tickets", action: onScrollToBanners, kind: "rose", testid: "hero-cta-tickets" },
      { label: "Support Business Showers", action: onScrollToForms, kind: "outline", testid: "hero-cta-support" },
    ];
    return [
      { label: "See Award Recipients", action: onScrollToPitches, kind: "amber", testid: "hero-cta-awards" },
      { label: "Next-Year Interest", action: onScrollToForms, kind: "outline", testid: "hero-cta-next" },
    ];
  }, [phase, live, onScrollToPitches, onScrollToForms, onScrollToBanners, onWatchLive, externalVotingUrl]);

  const handleAddPitchToCal = () => {
    const start = nextSession?.datetime || new Date(`${EVENT.pitchDays[0]}T19:00:00-04:00`);
    const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
    downloadICS("business-showers-pitch.ics", buildICS({
      title: "Business Showers — Live Pitches",
      description: "Live pitches from Freedom In Me Business Showers participating entrepreneurs.",
      startISO: start.toISOString(),
      endISO: end.toISOString(),
      location: "Online (Zoom)",
      url: "https://www.businessshowers.org/contest",
    }));
  };

  const phaseLabel = ["", "Coming Soon", "Live This Week", "Final Voting", "Event Week", "Highlights"][phase] || "";

  return (
    <section data-testid="hero-section" className="relative isolate overflow-hidden bg-emerald-950">
      {/* Background image with single solid dark overlay (no rainbow gradients) */}
      <img
        src={heroImage || HERO_BG_FALLBACK}
        alt=""
        aria-hidden
        className="absolute inset-0 -z-10 h-full w-full object-cover"
      />
      <div className="absolute inset-0 -z-10 bg-emerald-950/80" aria-hidden />

      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 sm:py-28 lg:py-32 text-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 space-y-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-300 px-4 py-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-950" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-emerald-950 font-[Outfit] font-bold">
                {phaseLabel}
              </span>
            </div>

            <h1 data-testid="hero-headline" className="font-[Cormorant_Garamond] text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-white">
              {headline}
            </h1>

            <p className="font-[Outfit] text-base sm:text-lg leading-relaxed text-emerald-50 max-w-2xl">
              {support}
            </p>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              {primaryCtas.map((c) => {
                const baseCls = "rounded-full px-6 h-12 font-[Outfit] font-semibold tracking-wide";
                const cls = c.kind === "amber"
                  ? "bg-amber-300 hover:bg-amber-400 text-emerald-950"
                  : c.kind === "rose"
                  ? "bg-rose-500 hover:bg-rose-600 text-white"
                  : "border-2 border-white bg-transparent text-white hover:bg-white hover:text-emerald-950";
                if (c.href) {
                  return (
                    <Button key={c.testid} asChild size="lg" data-testid={c.testid} className={`${baseCls} ${cls}`}>
                      <a href={c.href} {...(c.internal ? {} : { target: "_blank", rel: "noreferrer" })}>{c.label}</a>
                    </Button>
                  );
                }
                return (
                  <Button key={c.testid} onClick={c.action} size="lg" data-testid={c.testid}
                          variant={c.kind === "outline" ? "outline" : "default"}
                          className={`${baseCls} ${cls}`}>
                    {c.label}
                  </Button>
                );
              })}
            </div>

            {phase <= 2 && (
              <button
                data-testid="hero-add-to-cal"
                onClick={handleAddPitchToCal}
                className="text-xs uppercase tracking-[0.2em] text-amber-200 hover:text-amber-100 font-[Outfit] underline underline-offset-4"
              >
                ⌃ Add next pitch to calendar
              </button>
            )}

            {/* Social-proof: live volunteer count */}
            <div className="pt-1">
              <VolunteerCountBadge variant="pill" tone="dark" />
            </div>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {phase <= 2 && nextSession && (
              <div className="rounded-2xl bg-emerald-900 border-2 border-amber-300 p-6 sm:p-7">
                <Countdown
                  testid="countdown-next-pitch"
                  targetDate={nextSession.datetime}
                  label={`Next Pitch · ${new Date(nextSession.date+"T12:00:00-04:00").toLocaleDateString("en-US",{weekday:"long", month:"long", day:"numeric"})}`}
                />
              </div>
            )}
            {phase <= 3 && (
              <div className="rounded-2xl bg-rose-600 border-2 border-rose-300 p-6 sm:p-7">
                <Countdown
                  testid="countdown-voting-deadline"
                  targetDate={votingDeadline}
                  label="Voting Closes · June 22"
                />
              </div>
            )}
            {phase >= 4 && (
              <div className="rounded-2xl bg-emerald-900 border-2 border-amber-300 p-6 sm:p-7">
                <Countdown
                  testid="countdown-event"
                  targetDate={new Date(`${EVENT.mainCelebration}T18:00:00-04:00`)}
                  label="Until Main Celebration · July 18"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
